import PropTypes from 'prop-types';
import React from 'react';
import TEXT from '../../assets/constants';
import infoIcon from '../../assets/Images/infoIconLines.svg';

const InformationModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  };

  const modalStyle = {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    width: '70%',
    maxHeight: '80%',
    overflowY: 'auto',
    overflowX: 'hidden',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'none',
    border: 'none',
    fontSize: '50px',
    cursor: 'pointer',
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
    maxWidth: '800px',
    margin: 'auto',
  };

  const sectionStyle = {
    padding: '10px',
    boxSizing: 'border-box',
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ position: 'relative' }}>
          <button style={closeButtonStyle} onClick={onClose}>
            &times;
          </button>
          <div style={containerStyle}>
            <div style={sectionStyle}>
              <div className="grid-x align-middle">
                <img
                  alt="Info Icon"
                  className="display-inline-block padding-right-1"
                  height={50}
                  src={infoIcon}
                  width={50}
                />
                <h3 className="display-inline-block font-bold">
                  {TEXT.INFORMATION_BUTTON}
                </h3>
              </div>
            </div>

            <div style={sectionStyle}>
              <h3>Patient Assumptions</h3>
              <div style={rowStyle}>
                <span>Age</span>
                <span>40 years</span>
              </div>
              <div style={rowStyle}>
                <span>Baseline eGFR</span>
                <span>
                  90 mL/min/1.73 m<sup>2</sup>
                </span>
              </div>
              <div style={rowStyle}>
                <span>Dialysis Onset eGFR</span>
                <span>
                  10 mL/min/1.73 m<sup>2 3</sup>
                </span>
              </div>
            </div>

            <div style={sectionStyle}>
              <h3>Risk of CKD Progression</h3>
              <div style={rowStyle}>
                <span>Intermediate</span>
                <span>
                  -3.3 mL/min/1.73 m2/year <sup>1</sup>
                </span>
              </div>
              <div style={rowStyle}>
                <span>High</span>
                <span>
                  -5.9 mL/min/1.73 m2/year <sup>1</sup>
                </span>
              </div>
            </div>

            <div style={sectionStyle}>
              <h3>Treatment Plan</h3>
              <div style={rowStyle}>
                <span>RASi</span>
                <span>
                  15% reduction in decline rate <sup>5</sup>
                </span>
              </div>
              <div style={rowStyle}>
                <span>RASi + SGLT2i</span>
                <span>
                  50% reduction in decline rate <sup>4</sup>
                </span>
              </div>
              <div style={rowStyle}>
                <span>RASi + SGLT2i + MRA</span>
                <span>
                  66% reduction in decline rate <sup>6</sup>
                </span>
              </div>
              <div style={rowStyle}>
                <span>RAAS Inhibitor + SGLT2 Inhibitor + GLP1ra</span>
                <span>
                  68% reduction in decline rate <sup>6</sup>
                </span>
              </div>
              <div style={rowStyle}>
                <span>Four Pillars of Therapy (RAASi+SGLT2i+nsMRA+GLP1ra)</span>
                <span>
                  81% reduction in decline rate <sup>6</sup>
                </span>
              </div>
            </div>

            <div style={sectionStyle}>
              <h3>Intervention Scenarios</h3>
              <div style={rowStyle}>
                <span>Late</span>
                <span>
                  eGFR at Intervention Commencement - 44 mL/min/1.73 m
                  <sup>2 7</sup>
                </span>
              </div>
              <div style={rowStyle}>
                <span>Early</span>
                <span>
                  eGFR at Intervention Commencement - 80 mL/min/1.73 m
                  <sup>2</sup>
                </span>
              </div>
            </div>

            <div style={sectionStyle}>
              <p>References</p>
              <span>
                <sup>1</sup> Ferguson T et al. Development and external
                validation of a machine learning model for progression of CKD.
                Kidney Int Rep. 2022;7(8):1772-1781.
                doi:10.1016/j.ekir.2022.05.004
              </span>
              <br />
              <span>
                <sup>2</sup> Grams ME et al; CKD Prognosis Consortium.
                Development and validation of prediction models of adverse
                kidney outcomes in the population with and without diabetes.
                Diabetes Care. 2022;45(9):2055-2063. doi:10.2337/dc22-0698
              </span>
              <br />
              <span>
                <sup>3</sup> Sood M, et al.{' '}
                <span className="font-semibold">
                  Variation in the Level of eGFR at Dialysis Initiation across
                  Dialysis Facilities and Geographic Regions.{' '}
                </span>
                Clin J Am Soc Nephrol. 2014;9:1747-1756
              </span>
              <br />
              <span>
                <sup>4</sup> The EMPA KIDNEY Collaborative Group. Effects of
                empagliflozin on progression of chronic kidney disease: a
                prespecified secondary analysis from the EMPA-KIDNEY trial
                Lancet Diabetes Endocrinol. 2024 Jan;12(1):39-50
              </span>
              <br />
              <span>
                <sup>5</sup> Brenner BM, et al.{' '}
                <span className="font-semibold">
                  Effects of losartan on renal and cardiovascular outcomes in
                  patients with type 2 diabetes and nephropathy{' '}
                </span>
                N Engl J Med. 2001;345:861-869. )
              </span>
              <br />
              <span>
                <sup>6</sup> Bakris et al. A prespecified exploratory analysis
                from FIDELITY examined finerenone use and kidney outcomes in
                patients with chronic kidney disease and type 2 diabetes. Kidney
                Int. 2023 Jan;103(1):196-206
              </span>
              <br />
              <span>
                <sup>7</sup> Brimble et al. Impact of the KidneyWise toolkit on
                chronic kidney disease referral practices in Ontario primary
                care: a prospective evaluation. BMJ Open. 2020 Feb
                16;10(2):e032838
              </span>
              <br />
              <span>
                <sup>8</sup> Perkovic et al. Effect of semaglutide on Chronic
                Kidney Disease in Patients with Type 2 Diabetes. N Engl J Med.
                2024 Jul 11;391(2):109-121.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

InformationModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default InformationModal;
